import React, { useState, useEffect } from 'react';
import { CategoryDefinition } from '../../config/categoryConfig';
import StartCategoryButton from './StartCategoryButton';
import { cn } from '../../utils/cn';

interface StartCategoryCircleProps {
  categories: CategoryDefinition[];
  hoveredCategory: string | null;
  onCategoryHover: (slug: string | null) => void;
  onCategoryClick: (category: CategoryDefinition) => void;
  isCategoryLocked: (slug: string) => boolean;
}

/**
 * Calculate position for category button in circular layout
 * @param index - Category index (0-10)
 * @param total - Total number of categories (11)
 * @param radius - Circle radius in pixels (default: 280)
 * @returns {x, y} coordinates relative to center
 */
function getCircularPosition(index: number, total: number, radius: number) {
  const angleStep = 360 / total; // 32.727° for 11 categories
  const angle = (angleStep * index - 90) * (Math.PI / 180); // -90° to start at top

  const x = Math.cos(angle) * radius;
  const y = Math.sin(angle) * radius;

  return { x, y };
}

const StartCategoryCircle = React.memo(function StartCategoryCircle({
  categories,
  hoveredCategory,
  onCategoryHover,
  onCategoryClick,
  isCategoryLocked,
}: StartCategoryCircleProps) {
  const radius = 280; // pixels from center (22% reduction for compact layout)

  // Debounced hover state to prevent animation thrashing
  const [debouncedHoveredCategory, setDebouncedHoveredCategory] = useState<string | null>(hoveredCategory);

  // Debounce hover changes to prevent rapid re-animations
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedHoveredCategory(hoveredCategory);
    }, 100); // 100ms debounce

    return () => clearTimeout(timer);
  }, [hoveredCategory]);

  const hoveredCategoryData = categories.find((cat) => cat.slug === debouncedHoveredCategory);

  const centerLogoSrc = '/assets/site-logo/mini-transparent-neutral-logo.svg';

  return (
    <div className="relative w-full h-screen flex items-center justify-center">
      {/* Circle container */}
      <div className="relative" style={{ width: `${radius * 2 + 100}px`, height: `${radius * 2 + 100}px` }}>
        {/* Center content area */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center max-w-md px-8">
            {hoveredCategoryData ? (
              // Show hovered category description
              <div
                className="animate-slide-in-center bg-background rounded-2xl p-8 shadow-xl"
                key={hoveredCategoryData.slug}
              >
                <div
                  className="text-5xl mb-4"
                  role="img"
                  aria-label={hoveredCategoryData.displayName}
                >
                  {hoveredCategoryData.icon}
                </div>
                <h2 className="text-2xl font-bold mb-2 text-foreground">
                  {hoveredCategoryData.displayName}
                </h2>
                <p className="text-foreground">
                  {hoveredCategoryData.seoDescription}
                </p>
              </div>
            ) : (
              // Show logo when idle
              <div className="animate-slide-in-center bg-background rounded-2xl p-8 shadow-xl">
                <img
                  src={centerLogoSrc}
                  alt="Wallstreetle"
                  className="w-48 h-auto mx-auto mb-4"
                />
                <h2 className="text-2xl font-bold mb-2 text-foreground">
                  Welcome to Wallstreetle
                </h2>
                <p className="text-lg text-foreground">
                  Pick a stock category to play
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Category buttons in circular layout */}
        {categories.map((category, index) => {
          const position = getCircularPosition(index, categories.length, radius);
          const isLocked = isCategoryLocked(category.slug);
          const isHovered = hoveredCategory === category.slug;

          return (
            <div
              key={category.slug}
              className={cn(
                'absolute category-button',
                'animate-scale-in'
              )}
              style={{
                left: '50%',
                top: '50%',
                transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
                animationDelay: `${index * 80}ms`, // Staggered animation
              }}
              onMouseEnter={() => onCategoryHover(category.slug)}
              onMouseLeave={() => onCategoryHover(null)}
            >
              <StartCategoryButton
                category={category}
                isLocked={isLocked}
                isHovered={isHovered}
                onClick={() => onCategoryClick(category)}
                variant="circular"
                className="w-40"
              />
            </div>
          );
        })}
      </div>

      {/* Accessibility: Reduced motion support is handled via global CSS */}
    </div>
  );
});

export default StartCategoryCircle;
